package com.zq.webproject.ui.register

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.Font
import webproject.composeapp.generated.resources.Res
import webproject.composeapp.generated.resources.font_song

// 统一字体（使用宋体，包含完整中文字符）
private val CaiFont: androidx.compose.ui.text.font.FontFamily
    @Composable
    get() = androidx.compose.ui.text.font.FontFamily(Font(Res.font.font_song))

/**
 * 扫码注册界面
 * 专门用于处理用户扫描二维码后的注册流程
 * 
 * @param invitationCode 从URL参数获取的邀请码
 * @param onRegisterSuccess 注册成功后的回调（自动登录并跳转）  访问: http://localhost:8081/?inviteCode=TEST123
 *
 * 应该会看到：
 * ✅ 自动显示注册页面
 * ✅ 邀请码字段已填入 "TEST123"
 * ✅ 可以填写手机号、密码、验证码
 * ✅ 注册成功后自动登录并跳转首页
 * @param onBack 返回回调
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun QRCodeRegisterScreen(
    invitationCode: String? = null,
    onRegisterSuccess: () -> Unit = {},
    onBack: () -> Unit = {}
) {
    println("QRCodeRegisterScreen - 渲染扫码注册页面，邀请码: $invitationCode")
    
    val viewModel = remember { QRCodeRegisterViewModel() }
    
    // 设置邀请码
    LaunchedEffect(invitationCode) {
        if (!invitationCode.isNullOrEmpty()) {
            viewModel.setInvitationCode(invitationCode)
            println("QRCodeRegisterScreen - 设置邀请码: $invitationCode")
        }
    }
    
    // 监听注册成功事件
    LaunchedEffect(viewModel.isRegisterSuccess) {
        if (viewModel.isRegisterSuccess) {
            println("QRCodeRegisterScreen - 注册成功，自动登录并跳转")
            delay(500) // 短暂延迟让用户看到成功提示
            onRegisterSuccess()
        }
    }
    
    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = "新用户注册",
                        fontFamily = CaiFont,
                        fontWeight = FontWeight.Bold
                    )
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color(0xFF1E88E5),
                    titleContentColor = Color.White
                )
            )
        }
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
        ) {
            // 渐变背景
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(
                        brush = Brush.verticalGradient(
                            colors = listOf(
                                Color(0xFFE3F2FD),
                                Color.White
                            )
                        )
                    )
            )
            
            // 内容区域
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .padding(24.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Spacer(modifier = Modifier.height(32.dp))
                
                // 欢迎文字
                Text(
                    text = "欢迎加入！",
                    fontSize = 32.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFF1E88E5),
                    fontFamily = CaiFont
                )
                
                Spacer(modifier = Modifier.height(8.dp))
                
                Text(
                    text = "填写以下信息完成注册",
                    fontSize = 16.sp,
                    color = Color.Gray,
                    fontFamily = CaiFont
                )
                
                Spacer(modifier = Modifier.height(40.dp))
                
                // 注册表单卡片
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    colors = CardDefaults.cardColors(
                        containerColor = Color.White
                    ),
                    elevation = CardDefaults.cardElevation(4.dp)
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(24.dp)
                    ) {
                        // 手机号输入
                        OutlinedTextField(
                            value = viewModel.phone,
                            onValueChange = { viewModel.updatePhone(it) },
                            label = { Text("手机号", fontFamily = CaiFont) },
                            placeholder = { Text("请输入手机号", fontFamily = CaiFont) },
                            isError = viewModel.phoneError.isNotEmpty(),
                            supportingText = {
                                if (viewModel.phoneError.isNotEmpty()) {
                                    Text(
                                        text = viewModel.phoneError,
                                        color = Color.Red,
                                        fontFamily = CaiFont
                                    )
                                }
                            },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone)
                        )
                        
                        Spacer(modifier = Modifier.height(16.dp))
                        
                        // 密码输入
                        OutlinedTextField(
                            value = viewModel.password,
                            onValueChange = { viewModel.updatePassword(it) },
                            label = { Text("设置密码", fontFamily = CaiFont) },
                            placeholder = { Text("请设置6位以上密码", fontFamily = CaiFont) },
                            isError = viewModel.passwordError.isNotEmpty(),
                            supportingText = {
                                if (viewModel.passwordError.isNotEmpty()) {
                                    Text(
                                        text = viewModel.passwordError,
                                        color = Color.Red,
                                        fontFamily = CaiFont
                                    )
                                }
                            },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                            visualTransformation = PasswordVisualTransformation(),
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password)
                        )
                        
                        Spacer(modifier = Modifier.height(16.dp))
                        
                        // 验证码输入 + 获取验证码按钮
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            OutlinedTextField(
                                value = viewModel.code,
                                onValueChange = { viewModel.updateCode(it) },
                                label = { Text("验证码", fontFamily = CaiFont) },
                                placeholder = { Text("请输入验证码", fontFamily = CaiFont) },
                                isError = viewModel.codeError.isNotEmpty(),
                                modifier = Modifier.weight(1f),
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
                            )
                            
                            // 获取验证码按钮
                            Button(
                                onClick = { viewModel.sendVerificationCode() },
                                enabled = viewModel.canSendCode,
                                modifier = Modifier
                                    .height(56.dp)
                                    .width(110.dp),
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = Color(0xFF1E88E5),
                                    disabledContainerColor = Color(0xFF1E88E5).copy(0.3f)
                                )
                            ) {
                                Text(
                                    text = viewModel.sendCodeButtonText,
                                    fontSize = when {
                                        viewModel.isSendingCode -> 11.sp
                                        viewModel.countdown > 0 -> 20.sp
                                        else -> 11.sp
                                    },
                                    fontFamily = CaiFont,
                                    color = Color.White,
                                    fontWeight = FontWeight.Bold
                                )
                            }
                        }
                        
                        if (viewModel.codeError.isNotEmpty()) {
                            Text(
                                text = viewModel.codeError,
                                color = Color.Red,
                                fontSize = 12.sp,
                                modifier = Modifier.padding(start = 16.dp, top = 4.dp),
                                fontFamily = CaiFont
                            )
                        }
                        
                        Spacer(modifier = Modifier.height(16.dp))
                        
                        // 邀请码显示（只读）
                        OutlinedTextField(
                            value = viewModel.invitationCodeDisplay,
                            onValueChange = {},
                            label = { Text("邀请码", fontFamily = CaiFont) },
                            enabled = false,
                            modifier = Modifier.fillMaxWidth(),
                            colors = OutlinedTextFieldDefaults.colors(
                                disabledTextColor = Color.Gray,
                                disabledBorderColor = Color(0xFF1E88E5),
                                disabledLabelColor = Color(0xFF1E88E5)
                            ),
                            singleLine = true
                        )
                        
                        Spacer(modifier = Modifier.height(24.dp))
                        
                        // 注册按钮
                        Button(
                            onClick = { viewModel.register() },
                            enabled = !viewModel.isLoading,
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(50.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = Color(0xFF1E88E5)
                            )
                        ) {
                            if (viewModel.isLoading) {
                                CircularProgressIndicator(
                                    color = Color.White,
                                    modifier = Modifier.size(24.dp)
                                )
                            } else {
                                Text(
                                    text = "立即注册",
                                    fontSize = 18.sp,
                                    fontWeight = FontWeight.Bold,
                                    fontFamily = CaiFont
                                )
                            }
                        }
                        
                        // 错误信息显示
                        if (viewModel.errorMessage.isNotEmpty()) {
                            Spacer(modifier = Modifier.height(16.dp))
                            Text(
                                text = viewModel.errorMessage,
                                color = Color.Red,
                                fontSize = 14.sp,
                                textAlign = TextAlign.Center,
                                fontFamily = CaiFont,
                                modifier = Modifier.fillMaxWidth()
                            )
                        }
                        
                        // 成功信息显示
                        if (viewModel.successMessage.isNotEmpty()) {
                            Spacer(modifier = Modifier.height(16.dp))
                            Text(
                                text = viewModel.successMessage,
                                color = Color(0xFF4CAF50),
                                fontSize = 14.sp,
                                textAlign = TextAlign.Center,
                                fontFamily = CaiFont,
                                fontWeight = FontWeight.Bold,
                                modifier = Modifier.fillMaxWidth()
                            )
                        }
                    }
                }
                
                Spacer(modifier = Modifier.height(24.dp))
                
                // 已有账号提示
                TextButton(onClick = onBack) {
                    Text(
                        text = "已有账号？返回登录",
                        fontSize = 14.sp,
                        color = Color(0xFF1E88E5),
                        fontFamily = CaiFont
                    )
                }
            }
        }
    }
}
